import { parseDelimitedTable, type DelimitedRow } from './delimited.js';
import { looksLikeXlsx, readXlsxWorksheet } from './xlsx.js';

/**
 * One door for "the file the shop already has", whichever of the two shapes it arrived in.
 *
 * The distinction between a CSV and a workbook matters to a parser and to nobody else, so it
 * is settled here — by the bytes rather than by the extension, because a file renamed
 * `customers.csv` by somebody being helpful is still a workbook, and refusing it on the
 * strength of its name would be the product being pedantic about the one thing the owner
 * cannot see.
 */

/**
 * The ceiling on one import.
 *
 * Not a licence limit — a cost one. Every row is validated, checked against the shop's
 * existing numbers and written in a single transaction, and a transaction holding fifty
 * thousand inserts is one long-running write that can fail near the end and take the whole
 * import with it. A thousand rows imports in a few seconds and covers the list of all but the
 * largest shops; beyond that the honest answer is "split the file", said plainly and up front,
 * rather than a spinner that never finishes.
 */
export const MAX_IMPORT_ROWS = 1000;

/** Largest file accepted, before parsing. A customer list is text; anything larger is not. */
export const MAX_IMPORT_FILE_BYTES = 5 * 1024 * 1024;

export type SpreadsheetFormat = 'csv' | 'xlsx';

export interface SpreadsheetTable {
  readonly format: SpreadsheetFormat;
  /** Column headings, in file order. */
  readonly header: readonly string[];
  readonly rows: readonly DelimitedRow[];
  /** How many rows were dropped by {@link MAX_IMPORT_ROWS}, so the UI can say so. */
  readonly truncated: number;
}

/**
 * Read a spreadsheet out of the bytes a file input handed over.
 *
 * A workbook is recognised by its ZIP signature; everything else is treated as delimited text,
 * with the delimiter detected rather than assumed. The header is the first row that has any
 * content in it at all — exports routinely start with a blank line or two.
 */
export function readSpreadsheet(file: Uint8Array): SpreadsheetTable {
  if (looksLikeXlsx(file)) return fromWorksheet(file);

  const text = new TextDecoder().decode(file);
  const table = parseDelimitedTable(text);
  return limit({
    format: 'csv',
    header: table.header,
    rows: table.rows,
    truncated: 0,
  });
}

function fromWorksheet(file: Uint8Array): SpreadsheetTable {
  const sheet = readXlsxWorksheet(file);
  const headerIndex = sheet.rows.findIndex((row) => row.some((cell) => cell.trim().length > 0));
  if (headerIndex < 0) return { format: 'xlsx', header: [], rows: [], truncated: 0 };

  const header = (sheet.rows[headerIndex] ?? []).map((cell) => cell.trim());
  const rows: DelimitedRow[] = [];
  for (let index = headerIndex + 1; index < sheet.rows.length; index += 1) {
    const cells = (sheet.rows[index] ?? []).map((cell) => cell.trim());
    if (cells.every((cell) => cell.length === 0)) continue;
    while (cells.length < header.length) cells.push('');
    // The sheet's own row numbering, so "row 14" is the row Excel shows as 14.
    rows.push({ rowNumber: index + 1, cells });
  }

  return limit({ format: 'xlsx', header, rows, truncated: 0 });
}

function limit(table: SpreadsheetTable): SpreadsheetTable {
  if (table.rows.length <= MAX_IMPORT_ROWS) return table;
  return {
    ...table,
    rows: table.rows.slice(0, MAX_IMPORT_ROWS),
    truncated: table.rows.length - MAX_IMPORT_ROWS,
  };
}
